import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Attach JWT token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// AUTH
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');
export const changePassword = (data) => api.post('/auth/change-password', data);

// FACTORIES
export const getFactories = () => api.get('/factories');
export const createFactory = (data) => api.post('/factories', data);
export const updateFactory = (id, data) => api.put(`/factories/${id}`, data);
export const deleteFactory = (id) => api.delete(`/factories/${id}`);
export const getFactoryUsers = (id) => api.get(`/factories/${id}/users`);
export const reorderFactoriesApi = (factoryIds) => api.put('/factories/reorder', { factoryIds });

// FACTORY CATEGORIES
export const getFactoryCategories = () => api.get('/factory-categories');
export const createFactoryCategory = (data) => api.post('/factory-categories', data);
export const updateFactoryCategory = (id, data) => api.put(`/factory-categories/${id}`, data);
export const deleteFactoryCategory = (id, force = false) =>
  api.delete(`/factory-categories/${id}`, { params: { force } });
export const reorderFactoryCategoriesApi = (categoryIds) =>
  api.put('/factory-categories/reorder', { categoryIds });

// USERS
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// PRODUCTS
export const getProducts = (factoryId, includeInactive = false, view) =>
  api.get(`/factories/${factoryId}/products`, { params: { include_inactive: includeInactive, view } });
export const createProduct = (factoryId, data) => api.post(`/factories/${factoryId}/products`, data);
export const updateProduct = (factoryId, id, data) => api.put(`/factories/${factoryId}/products/${id}`, data);
export const deleteProduct = (factoryId, id) => api.delete(`/factories/${factoryId}/products/${id}`);
export const reorderProducts = (factoryId, productIds) => api.put(`/factories/${factoryId}/products/reorder-products`, { productIds });
export const reorderEntryProducts = (factoryId, productIds) => api.put(`/factories/${factoryId}/products/reorder-entry-products`, { productIds });
export const bulkCreateProducts = (factoryId, data) => api.post(`/factories/${factoryId}/products/bulk`, data);

// CATEGORIES
export const getCategories = (factoryId) => api.get(`/factories/${factoryId}/categories`);
export const createCategory = (factoryId, data) => api.post(`/factories/${factoryId}/categories`, data);
export const updateCategory = (factoryId, id, data) => api.put(`/factories/${factoryId}/categories/${id}`, data);
export const deleteCategory = (factoryId, id) => api.delete(`/factories/${factoryId}/categories/${id}`);
export const reorderCategoriesApi = (factoryId, categoryIds) => api.put(`/factories/${factoryId}/categories/reorder`, { categoryIds });
export const reorderEntryCategoriesApi = (factoryId, categoryIds) => api.put(`/factories/${factoryId}/categories/reorder-entry`, { categoryIds });

// PACKAGINGS (global, shared across factories)
export const getPackagings = () => api.get('/packagings');
export const createPackaging = (data) => api.post('/packagings', data);
export const updatePackaging = (id, data) => api.put(`/packagings/${id}`, data);
export const deletePackaging = (id, force = false) =>
  api.delete(`/packagings/${id}`, { params: { force } });
export const reorderPackagings = (packagingIds) => api.put('/packagings/reorder', { packagingIds });

// BAKERS (Ζαχαροπλάστες — global, shared across factories)
export const getBakers = () => api.get('/bakers');
export const createBaker = (data) => api.post('/bakers', data);
export const updateBaker = (id, data) => api.put(`/bakers/${id}`, data);
export const deleteBaker = (id, force = false) =>
  api.delete(`/bakers/${id}`, { params: { force } });
export const reorderBakers = (bakerIds) => api.put('/bakers/reorder', { bakerIds });

// ENTRIES
export const getEntries = (factoryId, params) => api.get(`/factories/${factoryId}/entries`, { params });
export const getEntry = (factoryId, date) => api.get(`/factories/${factoryId}/entries/${date}`);
export const saveEntry = (factoryId, data) => api.post(`/factories/${factoryId}/entries`, data);
export const deleteEntry = (factoryId, date) => api.delete(`/factories/${factoryId}/entries/${date}`);

// REPORTS — bakerId is optional; pass falsy to get unfiltered (Γενικά)
export const getMonthlyReport = (factoryId, year, month, bakerId) =>
  api.get(`/factories/${factoryId}/reports/monthly`, { params: bakerId ? { year, month, baker_id: bakerId } : { year, month } });
export const getAllMonthlyReport = (year, month) =>
  api.get(`/reports/all-monthly`, { params: { year, month } });
export const compareEntries = (factoryId, dates, bakerId) =>
  api.get(`/factories/${factoryId}/reports/compare`, { params: bakerId ? { dates: dates.join(','), baker_id: bakerId } : { dates: dates.join(',') } });
export const compareRanges = (factoryId, ranges, bakerId) =>
  api.post(`/factories/${factoryId}/reports/compare-ranges`, bakerId ? { ranges, baker_id: bakerId } : { ranges });
export const getYearlySummary = (factoryId, year, bakerId) =>
  api.get(`/factories/${factoryId}/reports/yearly-summary`, { params: bakerId ? { year, baker_id: bakerId } : { year } });
export const getTopProducts = (factoryId, params) =>
  api.get(`/factories/${factoryId}/reports/top-products`, { params });
